import type * as LDClient from 'launchdarkly-js-client-sdk';
import { useFlags } from 'launchdarkly-react-client-sdk';
import {
  AppEnv,
  GlobalEnvironment,
  useGlobalEnvironment,
} from '#app/shared/contexts/AppContext';
import {
  type AuthContextResult,
  KeyAnonymousUser,
  KeyUnknownUser,
  ValueUnknown,
} from '#app/shared/contexts/AuthContext';
import type {
  FeatureFlagMap,
  FeatureFlags,
} from '#app/shared/types/FeatureFlags';

export const getLDOptionsFromAuthContext = (
  context: AuthContextResult | null,
) => {
  const custom = { ...context?.externalContext };
  const defaultValue = !context ? KeyUnknownUser : KeyAnonymousUser;

  // remove email as it's part of ldOptions.
  // NOTE: passing legacy `group` attribute for current LD rules.
  custom.email = undefined;

  const { userKey, userFirstName, userLastName, userEmail } = context ?? {};

  const ldIdentifyOptions = {
    key: userKey ?? defaultValue,
    email: userEmail ?? ValueUnknown,
    firstName: userFirstName ?? ValueUnknown,
    lastName: userLastName ?? ValueUnknown,
    custom,
  };

  return ldIdentifyOptions;
};

/**
 * Set the LaunchDarkly user identity (identify) based upon the AuthContextResult.
 * @param context
 * @param ldClient
 * @returns
 */
export const setLDIdentifyUserFromContext = async (
  context: AuthContextResult,
  ldClient?: LDClient.LDClient,
) => {
  if (!ldClient) {
    return;
  }

  const options = getLDOptionsFromAuthContext(context);
  // eslint-disable-next-line no-console
  console.log('LD', { options });
  try {
    await ldClient.identify(options);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error identifying user with LaunchDarkly', error);
  }
};

/**
 * Default flag configuration used when no response from LaunchDarkly.
 */
export const DefaultFlags: LDClient.LDFlagSet = {
  // Add our Feature Flag's default configuration here.
};

export const useOverridableFlags = <T extends FeatureFlagMap = FeatureFlagMap>(
  flags: Array<FeatureFlags>,
): T => {
  const flagsStates = useFlags<FeatureFlagMap>();
  const globalEnvironment = useGlobalEnvironment();
  if (GlobalEnvironment.APP_ENV !== AppEnv.LOCAL) {
    return flagsStates as T;
  }
  const overridesStr = globalEnvironment.FEATURE_FLAG_OVERRIDES;
  if (overridesStr) {
    const newflags: Record<string, boolean> = {};
    for (const flag in flagsStates) {
      newflags[flag] = (flagsStates as Record<string, boolean>)[flag];
    }
    const overrides = JSON.parse(overridesStr);
    for (const flag of flags) {
      const flagName = flag.toString();
      if (flagName in overrides) {
        newflags[flag] = overrides[flag];
      }
    }
    return newflags as T;
  }
  return flagsStates as T;
};
